import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const TeamMemberModal = ({ member, onClose }) => {
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: member?.username || '',
    email: member?.email || '',
    role: member?.role || 'support',
    is_active: member?.is_active !== false,
    send_invite: !member
  });
  const [loading, setLoading] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      const url = member
        ? `${API_BASE_URL}/api/admin/team/${member.id}`
        : `${API_BASE_URL}/api/admin/team`;
      
      const method = member ? 'PUT' : 'POST';
      const body = member
        ? { name: formData.name, role: formData.role, is_active: formData.is_active }
        : { name: formData.name, email: formData.email, role: formData.role, send_invite: formData.send_invite };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        if (!member && data.member?.temporary_password) {
          toast({
            title: "✅ Team Member Created",
            description: `Temporary Password: ${data.member.temporary_password}\n\nPlease save this password securely.`,
            duration: 10000,
          });
        } else {
          toast({
            title: member ? "✅ Team Member Updated" : "✅ Team Member Created",
            description: member ? 'Team member updated successfully!' : 'Team member created successfully!',
            duration: 3000,
          });
        }
        onClose();
      } else {
        const error = await response.json();
        toast({
          title: "Save Failed",
          description: error.detail || 'Failed to save team member',
          variant: "destructive",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error saving team member:', error);
      toast({
        title: "Save Error",
        description: 'Failed to save team member. Please try again.',
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm(`Are you sure you want to reset the password for ${member?.username}?`)) return;

    setResettingPassword(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/team/${member.id}/reset-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.temporary_password) {
          toast({
            title: "🔑 Password Reset Successful",
            description: `Temporary Password: ${data.temporary_password}\n\nPlease save this and share it securely.`,
            duration: 10000,
          });
        } else {
          toast({
            title: "✅ Password Reset Email Sent",
            description: 'Password reset instructions sent to team member.',
            duration: 3000,
          });
        }
      } else {
        const error = await response.json();
        toast({
          title: "Reset Failed",
          description: error.detail || 'Failed to reset password',
          variant: "destructive",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast({
        title: "Reset Error",
        description: 'Failed to reset password. Please try again.',
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setResettingPassword(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-md w-full p-6"
           style={{
             background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)',
             border: '1px solid rgba(255, 182, 193, 0.3)',
           }}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-500 to-violet-600 bg-clip-text text-transparent">
            {member ? 'Edit Team Member' : 'Add Team Member'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-purple-300"
            />
          </div>

          {!member && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-purple-300"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-purple-300"
            >
              <option value="admin">Admin</option>
              <option value="support">Support</option>
            </select>
          </div>

          {member && (
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-purple-500 rounded focus:ring-purple-400"
                />
                <span className="text-sm font-medium text-gray-700">Active</span>
              </label>
            </div>
          )}

          {!member && (
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.send_invite}
                  onChange={(e) => setFormData({ ...formData, send_invite: e.target.checked })}
                  className="w-4 h-4 text-purple-500 rounded focus:ring-purple-400"
                />
                <span className="text-sm font-medium text-gray-700">Send invite email</span>
              </label>
            </div>
          )}

          {member && (
            <div className="p-4 bg-gradient-to-br from-yellow-50 to-orange-50 border border-orange-200 rounded-xl">
              <p className="text-sm font-medium text-gray-700 mb-2">🔑 Password Reset</p>
              <button
                type="button"
                onClick={handleResetPassword}
                disabled={resettingPassword}
                className="px-4 py-2 bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
              >
                {resettingPassword ? 'Resetting...' : 'Reset Password'}
              </button>
              <p className="text-xs text-gray-600 mt-2">
                Generates a new temporary password for this team member.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-400 to-violet-500 text-white font-medium disabled:opacity-50 hover:shadow-lg transition-all"
            >
              {loading ? 'Saving...' : (member ? 'Update' : 'Create')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamMemberModal;